import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Provider } from 'react-redux';

import store from './redux/store';
import { getUser, refreshAccessToken } from './utils/helpers';
import Preloader from './components/Global/Preloader';
import { csrfService } from './services/csrfService';


import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import GitHubCallback from './components/Auth/GitHubCallback';
import OAuthCallback from './components/Auth/OAuthCallback';
import Workspaces from './components/Workspaces';
import UserProfile from './components/Profile/UserProfile';
import Workspace from './components/Profile/Workspace';
import Boards from './components/Profile/Boards';
import Cards from './components/Profile/CardTable';
import Collaborators from './components/Profile/Collaborators';
import Settings from './components/Profile/Settings';
import WhiteLabelSettings from './components/Profile/WhiteLabelSettings';
import Notifications from './components/Workspaces/Notifications';

import Modal from './components/Global/ModalManager';
import PopOver from './components/Global/PopOver';

import ExpandableColumn from './components/Global/ExpandableColumn';
import TimerManager from './components/TimerManager/TimerManager';
import TimerToggleButton from './components/TimerManager/TimerToggleButton';

import { BoardProvider } from './context/BoardContext';

import ExportImport from './components/Profile/ExportImport';
import BoardWrapper from './components/Workspaces/BoardWrapper';
import Activity from './components/Profile/Activity';

import './App.scss';
import './components/Global/enhanced-scrollbar.css';
import SnackBar from './components/Global/SnackBar';
import VerificationSuccess from './components/Auth/VerificationSuccess';
import VerificationError from './components/Auth/VerificationError';
import VerificationRequired from './components/Auth/VerificationRequired';
import { fetchUser } from './redux/Slices/thunks';
import Reset from './components/Auth/Reset';
import RecoveryMail from './components/Auth/RecoveryMail';
import Verify from './components/Auth/Verify';
import AcceptInvitationRequest from './components/Members/AcceptInvitationRequest';
import SetPassword from './components/Auth/SetPassword';
import SetPasswordBanner from './components/Auth/PasswordBanner';
import { fetchWorkspaces } from './redux/Slices/workspaceSlice';
import { fetchRunningTimers } from './redux/Slices/timerSlice';
import FeedbackButton from './components/Global/FeedbackButton';
import Home from './pages/static-pages/landing/Home';
import Tooltip from './components/Global/Tooltip';
import MetaTags from './components/Global/MetaTags';
import OnlineStatusProvider from './context/OnlineStatusContext';
import PushNotification from './components/Global/PushNotification';
import Slack from './components/Auth/Slack';
import CLIAuth from './components/Auth/CLIAuth';
import FontProvider from './components/FontProvider';
import Policy from './pages/static-pages/legal/Policy';
import Terms from './pages/static-pages/legal/Terms';
import OnboardingFlow from './components/Import/OnboardingFlow';
import ImportProgress from './components/Import/ImportProgress';
import Pricing from './pages/Billing';
import PaymentCancelled from './pages/PaymentCancel';
import NetworkStatus from './components/Global/NetworkStatus';
import AnalyticsPage from './components/Analytics/AnalyticsPage';
import AutomationPage from './components/Automation/AutomationPage';
import AutomationRuleBuilder from './components/Automation/AutomationRuleBuilder';
import { config } from './config';
import AIChatbot from './components/AI/AIChatbot';
import NotFound from './components/400';
import DomainDebug from './components/Global/DomainDebug';
import Sidebar from './components/Sidebar';
import MobileBottomNav from './components/Sidebar/MobileBottomNav';
import GlobalNavbar from './components/Global/GlobalNavbar';
import FeaturesComparison from './pages/static-pages/landing/FeaturesComparison';
import About from './pages/static-pages/landing/About';
import Changelog from './pages/static-pages/landing/Changelog';
import ThemeDebugger from './components/Debug/ThemeDebugger';
import Contact from './pages/static-pages/landing/Contact';
import FeatureRequest from './pages/static-pages/landing/FeatureRequest';
import Roadmap from './pages/static-pages/landing/Roadmap';
import { useWhiteLabel } from './context/WhiteLabelContext';
import WorkspaceSettings from './components/Profile/WorkspaceSettings';
import WorkspaceMedia from './components/Workspaces/WorkspaceMedia/WorkspaceMedia';
import CrispChat from './components/Global/CrispChat';
import NeuronChat from './components/Global/NeuronChat';
import MicrosoftClarity from './components/Global/MicrosoftClarity';
import ProgressHandler from './components/Global/ProgressHandler';
import PageList from './components/Pages/PageList';
import PageView from './components/Pages/PageView';
import PublicPageViewer from './components/Pages/PublicPageViewer';
import WhatsNew from './pages/WhatsNew';

import { closeModal } from './redux/Slices/modalSlice';
import { togglePopover } from './redux/Slices/popoverSlice';

const ProtectedRoute = ({ element: Component, loggedIn, verified, redirectTo = "/login", ...rest }) => {
  if (!loggedIn) {
    // Preserve current URL so user returns here after login (e.g., /cli/auth?state=xxx&port=xxx)
    const returnTo = window.location.pathname + window.location.search;
    const loginUrl = returnTo && returnTo !== '/'
      ? `${redirectTo}?returnTo=${encodeURIComponent(returnTo)}`
      : redirectTo;
    return <Navigate to={loginUrl} />;
  }
  if (!verified) return <Navigate to="/verification-required" />;
  return <Component {...rest} />;
};

// Component to prevent logged-in users from accessing static pages
const StaticPageRoute = ({ element: Component, loggedIn, userProfileBoardsLink, ...rest }) => {
  // If logged in, redirect to user boards
  if (loggedIn) {
    return <Navigate to={userProfileBoardsLink} />;
  }
  // Otherwise, show the static page
  return <Component {...rest} />;
};

// Component to handle home page routing based on white label status
const HomeOrLogin = ({ loggedIn, verified, userProfileBoardsLink }) => {
  const { isWhiteLabel: isWL } = useWhiteLabel();

  // If logged in, redirect to user boards
  if (loggedIn) {
    return <ProtectedRoute element={() => <Navigate to={userProfileBoardsLink} />} loggedIn={loggedIn} verified={verified} />;
  }

  // If white label domain, redirect to login instead of showing home page
  if (isWL()) {
    return <Navigate to="/login" />;
  }

  // Default: show home page for non-white-label domains
  return <Home />;
};

const UserProfileRedirect = () => {
  const { userName } = useParams();
  return <Navigate to={`/u/${userName}/profile`} replace />;
};

// The old DealMirror LTD landing page is retired — lifetime tiers are now sold
// first-party on the billing page's Lifetime tab. Send visitors there.
const LifetimeRedirect = ({ loggedIn }) => {
  const { workspaces } = useSelector((state) => state.workspaces);
  if (!loggedIn) return <Navigate to="/login" replace />;
  const target = (workspaces || []).find((w) => w.shortId);
  return <Navigate to={target ? `/w/${target.shortId}/billing?tab=lifetime` : '/'} replace />;
};

function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [verified, setVerified] = useState();
  const [loading, setLoading] = useState(true);
  const [userProfileBoardsLink, setUserProfileBoardsLink] = useState('');
  const isCheckingLoginStatus = useRef(false);
  const { user } = useSelector((state) => state.user);
  const { settings } = user?.user || {};


  const dispatch = useDispatch();

  // Initialize CSRF protection on app start
  useEffect(() => {
    const initializeSecurity = async () => {
      try {
        await csrfService.initialize();
        console.log('✅ CSRF protection initialized');
      } catch (error) {
        console.warn('⚠️ CSRF protection initialization failed:', error);
        // App continues to work, CSRF tokens will be fetched on-demand
      }
    };

    initializeSecurity();
  }, []);

  useEffect(() => {
    const checkLoginStatus = async () => {
      if (isCheckingLoginStatus.current) return;

      isCheckingLoginStatus.current = true;
      const data = await getUser();

      if (data?.user?._id) {
        setLoggedIn(true);
        setVerified(data.user.verified || false);
        setUserProfileBoardsLink(`/u/${data.user.username}/boards`);
      }

      setLoading(false);
    };

    checkLoginStatus();
  }, [dispatch, user]);

  useEffect(() => {
    if (!user) {
      dispatch(fetchUser());
    }
  }, [dispatch]);

  const { workspaces } = useSelector(state => state.workspaces);

  useEffect(() => {
    if (loggedIn && workspaces.length === 0) {
      dispatch(fetchWorkspaces()).unwrap().then((fetchedWorkspaces) => {
        if (!fetchedWorkspaces || fetchedWorkspaces.length === 0) {
        }
      }).catch((error) => {
        console.error('Error fetching workspaces:', error);
      });
    }
  }, [dispatch, workspaces.length]);

  // Fetch running timers when user is logged in
  useEffect(() => {
    if (loggedIn && verified) {
      dispatch(fetchRunningTimers()).catch((error) => {
        console.error('Error fetching running timers:', error);
      });
    }
  }, [loggedIn, verified, dispatch]);

  useEffect(() => {
    if (!settings) return;
    const root = document.documentElement;
    root.setAttribute("data-theme-color", settings.themeColor);

  }, [user]);

  if (loading) {
    return <Preloader loading={loading} />; // You might want to display a loading spinner here
  }

  return (
    <Provider store={store}>
      <FontProvider>
        <Router>
          <OnlineStatusProvider>
            <AppContent loggedIn={loggedIn} verified={verified} userProfileBoardsLink={userProfileBoardsLink} user={user} />
          </OnlineStatusProvider>
        </Router>
      </FontProvider>
    </Provider>
  );
}

const AppContent = ({ loggedIn, verified, userProfileBoardsLink, user }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const modalIsOpen = useSelector((state) => state.modal.isOpen);
  const popoverState = useSelector((state) => state.popover);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (modalIsOpen) {
          dispatch(closeModal());
        }
        // Only close popover if it's not pinned
        if (popoverState?.isVisible && !popoverState?.props?.pinned) {
          dispatch(togglePopover({
            contentId: popoverState.contentId,
            position: popoverState.position,
            targetId: popoverState.targetId,
            props: popoverState.props,
          }));
        }
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [dispatch, modalIsOpen, popoverState?.isVisible, popoverState?.contentId, popoverState?.targetId, popoverState?.position, popoverState?.props]);


  // Split the path and get the first non-empty segment
  const pathParts = location.pathname.split('/').filter(Boolean);

  // Define a mapping for segments to the desired pageId
  const pageIdMapping = {
    'c': 'card-page',
    'w': 'workspace-page',
    'u': 'user-page',
    'b': 'board-page',
    'boards': 'user-boards-page',
    'profile': 'user-page',
    'cards': 'card-page',
    'activity': 'activity-page',
    'settings': 'settings-page',
    'white-label': 'white-label-page',
    'members': 'members-page',
    'notifications': 'notifications-page',
    'billing': 'billing-page',
    'import': 'import-page',
    'progress': 'import-progress-page',
    'analytics': 'analytics-page',
    'automations': 'automations-page',
    'login': 'login-page',
    'signup': 'signup-page',
    'pages': 'pages-page',
    'whats-new': 'whats-new-page',
  };

  // Walk path segments from last to first, use the first match
  let pageId = 'home';
  for (let i = pathParts.length - 1; i >= 0; i--) {
    if (pageIdMapping[pathParts[i]]) {
      pageId = pageIdMapping[pathParts[i]];
      break;
    }
  }

  // Clear board background when navigating away from board/card pages
  useEffect(() => {
    if (pageId !== 'board-page' && pageId !== 'card-page') {
      const mainEl = document.querySelector('main.app-content');
      mainEl?.style.removeProperty('--background-url');
    }
  }, [pageId]);

  // Force dark mode for non-logged-in users (except signup/login pages)
  useEffect(() => {
    if (loggedIn) return;

    // Don't force dark mode on signup/login pages
    // const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';
    // if (isAuthPage) return;

    // Add dark mode class to <html>
    document.documentElement.classList.add('dark-mode');
    document.documentElement.setAttribute('data-theme', 'dark');

    // Cleanup: remove when leaving the page
    return () => {
      document.documentElement.classList.remove('dark-mode');
      document.documentElement.removeAttribute('data-theme');
    };
  }, [loggedIn, location.pathname]);



  return (
    <main id={pageId} className="app-content" data-app-version={config.version} >
      {(loggedIn && pageId !== 'billing-page') && <Sidebar />}
      {(loggedIn && pageId !== 'billing-page') && <MobileBottomNav />}
      {(loggedIn && pageId !== 'billing-page') && <ExpandableColumn />}


      <BoardProvider>
          <div className="app-content-wrapper">
            {user && !user?.user?.isDefaultPasswordSet && !user?.user?.registeredWithGoogle && <SetPasswordBanner />}

            {/* <GlobalNavbar /> */}
          {/* <ProgressHandler /> */}

          <MetaTags />

          <Routes>
            <Route path="/workspace" element={<ProtectedRoute element={Workspace} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/" element={<HomeOrLogin loggedIn={loggedIn} verified={verified} userProfileBoardsLink={userProfileBoardsLink} />} />
            <Route path="/u/:userName" element={<UserProfileRedirect />} />
            <Route path="/u/:userName/profile" element={<ProtectedRoute element={UserProfile} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/u/:userName/boards" element={<ProtectedRoute element={Workspace} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/u/:userName/cards" element={<ProtectedRoute element={Cards} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/u/:userName/activity" element={<ProtectedRoute element={Activity} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/u/:userName/settings" element={<ProtectedRoute element={Settings} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/u/:userName/white-label" element={<ProtectedRoute element={WhiteLabelSettings} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/w/:workspaceSlug" element={<ProtectedRoute element={Workspaces} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/b/:boardId/:boardSlug" element={<ProtectedRoute element={BoardWrapper} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/b/:boardId/" element={<ProtectedRoute element={BoardWrapper} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/b/:boardId/analytics" element={<ProtectedRoute element={AnalyticsPage} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/b/:boardId/automations" element={<ProtectedRoute element={AutomationPage} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/b/:boardId/automations/new" element={<ProtectedRoute element={AutomationRuleBuilder} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/b/:boardId/automations/:automationId/edit" element={<ProtectedRoute element={AutomationRuleBuilder} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/c/:cardId/:cardSlug" element={<ProtectedRoute element={BoardWrapper} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/c/:cardId/" element={<ProtectedRoute element={BoardWrapper} loggedIn={loggedIn} verified={verified} />} />

            <Route path="/w/:shortId/boards" element={<ProtectedRoute element={Boards} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/w/:shortId/members" element={<ProtectedRoute element={Collaborators} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/w/:shortId/settings" element={<ProtectedRoute element={WorkspaceSettings} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/w/:shortId/media" element={<ProtectedRoute element={WorkspaceMedia} loggedIn={loggedIn} verified={verified} />} />

            {/* Page routes */}
            <Route path="/w/:workspaceId/pages" element={<ProtectedRoute element={PageList} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/w/:workspaceId/pages/:pageId" element={<ProtectedRoute element={PageView} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/p/:pageId" element={<ProtectedRoute element={PageView} loggedIn={loggedIn} verified={verified} />} />

            {/* Public page viewer (no auth required) */}
            <Route path="/share/:pageId" element={<PublicPageViewer />} />

            <Route path="/notifications" element={<ProtectedRoute element={Notifications} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/whats-new" element={<ProtectedRoute element={WhatsNew} loggedIn={loggedIn} verified={verified} />} />

            <Route path="/login" element={!loggedIn ? <Login /> : <Navigate to={userProfileBoardsLink} />} />
            <Route path="/signup" element={!loggedIn ? <Signup /> : <Navigate to={userProfileBoardsLink} />} />
            <Route path="/auth/github/callback" element={<GitHubCallback />} />
            <Route path="/auth/github/callback/" element={<GitHubCallback />} />
            <Route path="/auth/oauth/callback" element={<OAuthCallback />} />
            <Route path="/auth/oauth/callback/" element={<OAuthCallback />} />
            <Route path="/verify" element={<Verify />} /> {/* Only one verify route */}
            <Route path="/recovery" element={!loggedIn ? <RecoveryMail /> : <Navigate to={userProfileBoardsLink} />} />
            <Route path="/reset" element={!loggedIn ? <Reset /> : <Navigate to={userProfileBoardsLink} />} />
            <Route path="/set-password" element={loggedIn ? <SetPassword /> : <Navigate to={'/login'} />} />

            <Route path="/slack/auth" element={loggedIn ? <Slack /> : <Navigate to={userProfileBoardsLink} />} />
            <Route path="/cli/auth" element={<ProtectedRoute element={CLIAuth} loggedIn={loggedIn} verified={verified} />} />

            <Route path="/invite/b/:boardId" element={<AcceptInvitationRequest />} />
            <Route path="/invite/w/:shortId" element={<AcceptInvitationRequest />} />

            <Route path="/legal/privacy-policy" element={<StaticPageRoute element={Policy} loggedIn={loggedIn} userProfileBoardsLink={userProfileBoardsLink} />} />
            <Route path="/legal/terms-of-service" element={<StaticPageRoute element={Terms} loggedIn={loggedIn} userProfileBoardsLink={userProfileBoardsLink} />} />
            <Route path="/features-comparison" element={<StaticPageRoute element={FeaturesComparison} loggedIn={loggedIn} userProfileBoardsLink={userProfileBoardsLink} />} />
            <Route path="/about" element={<StaticPageRoute element={About} loggedIn={loggedIn} userProfileBoardsLink={userProfileBoardsLink} />} />
            <Route path="/changelog" element={<StaticPageRoute element={Changelog} loggedIn={loggedIn} userProfileBoardsLink={userProfileBoardsLink} />} />
            <Route path="/roadmap" element={<StaticPageRoute element={Roadmap} loggedIn={loggedIn} userProfileBoardsLink={userProfileBoardsLink} />} />
            <Route path="/contact" element={<StaticPageRoute element={Contact} loggedIn={loggedIn} userProfileBoardsLink={userProfileBoardsLink} />} />
            <Route path="/feature-request" element={<StaticPageRoute element={FeatureRequest} loggedIn={loggedIn} userProfileBoardsLink={userProfileBoardsLink} />} />
            {/* Retired DealMirror LTD landing → first-party lifetime tab. */}
            <Route path="/dealmirror" element={<LifetimeRedirect loggedIn={loggedIn} />} />
            <Route path="/lifetime" element={<LifetimeRedirect loggedIn={loggedIn} />} />

            <Route path="/w/:shortId/billing" element={<ProtectedRoute element={Pricing} loggedIn={loggedIn} verified={verified} />} />
            <Route path="/w/:shortId/payment-cancelled" element={<ProtectedRoute element={PaymentCancelled} loggedIn={loggedIn} verified={verified} />} />


            {
              !verified && (
                <>
                  <Route path="/verification-error" element={<VerificationError />} />
                  <Route path="/verification-required" element={<VerificationRequired />} />
                </>
              )
            }
            {
              verified && (
                <Route path="/verification-success" element={<VerificationSuccess />} />
              )
            }

            <Route path="/import" element={<ProtectedRoute element={OnboardingFlow} loggedIn={loggedIn} verified={verified} />} />

            <Route path="/import/progress" element={<ProtectedRoute element={ImportProgress} loggedIn={loggedIn} verified={verified} />} />

            {/* Catch-all route for 404 pages */}
            {/* <Route path="*" element={<NotFound />} /> */}
          </Routes>

          <Modal />
          <SnackBar />
          <Tooltip />
          <PushNotification />
          <NetworkStatus />
          <TimerManager />
          <TimerToggleButton />
          <AIChatbot />
          {loggedIn && <NeuronChat />}
          {/* <MicrosoftClarity /> */}

          {/* {(!loggedIn) && <CrispChat />} */}
          {/* {(!loggedIn) && <MicrosoftClarity />} */}


          {/* <DomainDebug /> */}
          {/* <ThemeDebugger /> */}
        </div>

        <PopOver />
      </BoardProvider>
    </main>
  );
};

export default App;